import useOutsideClickAlerter from '../../utils/useOutsideClickAlerter';
import DatePickerInput from './datePickerInput';
import { createStyles, makeStyles } from '@mui/styles';
import { Colors, HtmlUtils } from 'copyright-clearance-center-common-ui';
import { FormItemBase, IFormItemBaseProps } from 'copyright-clearance-center-common-ui-react';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import Datetime from 'react-datetime';

export const useStyles = makeStyles(() =>
	createStyles({
		root: {
			'& > .rdtPicker': {
				border: 'none',
				boxShadow: '0 2px 2px rgba(0,0,0,.14), 0 0 3px rgba(0,0,0,.2)',
				borderRadius: '0.25rem',
				right: '0%',
				'& td.rdtDisabled': {
					color: Colors.neutral500
				}
			}
		},
		calendarContainer: {
			zIndex: '99999 !important',
			position: 'fixed'
		}
	})
);

export interface IDatePickerProps extends IFormItemBaseProps {
	value?: Date;
	format?: string;
	placeholder?: string;
	mask?: string;
	localeName: string;
	onDateChanged?: (date: Date | undefined) => void;
	minDate?: Date;
	maxDate?: Date;
	closeOnSelect?: boolean;
	testId?: string;
	preserveInvalidValue?: boolean;
	calendarDateFormat?: string;
}

function positionCalendar(root: HTMLElement, calendar: HTMLElement, height: number): void {
	const rootRect = root.getBoundingClientRect();
	const documentOffset = HtmlUtils.offset(root);
	const calendarRect = calendar.getBoundingClientRect();
	let align = 'left';

	if (calendarRect.width + rootRect.left > window.innerWidth) {
		align = 'right';
	}

	if (align === 'left') {
		calendar.style.left = `${documentOffset.left}px`;
	} else {
		calendar.style.left = `${rootRect.right - calendarRect.width}px`;
	}

	if (window.innerHeight > rootRect.bottom + height) {
		calendar.style.maxHeight = undefined;
		calendar.style.bottom = 'unset';
		calendar.style.top = `${rootRect.bottom + 1}px`;
		calendar.classList.remove('up');
	} else {
		const maxHeight = rootRect.top - 1;
		if (maxHeight < height) {
			calendar.style.maxHeight = `${maxHeight}px`;
		} else {
			calendar.style.maxHeight = 'unset';
		}

		const bottom = innerHeight - rootRect.top + 1;
		calendar.style.top = 'unset';
		calendar.style.bottom = `${bottom}px`;
		calendar.classList.add('up');
	}
}

/**
 * This date picker component is designed to render the picker outside of a scrollable container,
 * preventing any overlap with scrollable container.
 * The calendar container's "position" CSS rule is set to "fixed",
 * and it is manually positioned directly under or above the input element using HtmlUtils.positionDropdown function.
 * When the scrolling event appears, the dropdown is closed just to avoid re-positioning.
 */
const DatePicker: React.FunctionComponent<IDatePickerProps> = props => {
	const classes = useStyles();
	const containerWrapper = useRef<HTMLDivElement>(null);
	const inputWrapper = useRef<HTMLDivElement>(null);
	const dropdownWrapper = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState<boolean>(undefined);
	const [value, setValue] = useState<Date>(undefined);
	const [inputValue, setInputValue] = useState<Date>(undefined);
	const [refreshSignal, setRefreshSignal] = useState(0);
	const height = useRef(0);

	useOutsideClickAlerter(containerWrapper, () => setOpen(false));

	useEffect(() => {
		const handleScroll = (() => setOpen(false)).bind(this);
		window.addEventListener('scroll', handleScroll, true);

		return () => {
			window.removeEventListener('scroll', handleScroll, true);
		};
	}, []);

	useEffect(() => {
		if (value !== props.value) {
			setValue(props.value);
			setInputValue(props.value);
			setRefreshSignal(refreshSignal + 1);
		}
	}, [props.value]);

	useEffect(() => {
		if (open) {
			dropdownWrapper.current.style.display = 'block';

			if (height.current === 0) {
				height.current = dropdownWrapper.current.getBoundingClientRect().height;
			}

			positionCalendar(inputWrapper.current, dropdownWrapper.current, height.current);
		} else {
			if (open !== undefined) {
				setInputValue(value);
				setRefreshSignal(refreshSignal + 1);
			}

			dropdownWrapper.current.style.display = 'none';
		}
	}, [open]);

	const onCalendarChange = (newValue: string | moment.Moment): void => {
		const dateValue = typeof newValue === 'string' ? moment(newValue).toDate() : newValue?.toDate();
		setValue(dateValue);
		setInputValue(dateValue);
		props.onDateChanged?.call(this, dateValue);
		if (props.closeOnSelect !== false) {
			setOpen(false);
		}
	};

	const onInputChange = (date: Date | undefined) => {
		if (date && !isDateValid(date)) {
			date = props.preserveInvalidValue ? new Date('') : undefined;
		}

		setValue(date);
		props.onDateChanged?.call(this, date);
	};

	const isDateValid = (date: Date): boolean => {
		// Date from picker has 23:59:59 in hours section,
		// while our min or max dates don't have that, so we normalize it.
		const fullYear = date.getFullYear();
		const normalizedDate = new Date(fullYear, date.getMonth(), date.getDate(), 0, 0, 0, 0);
		// Fix issue with 2-digits year conversion.
		normalizedDate.setFullYear(fullYear);

		return !(
			(props.minDate && normalizedDate < props.minDate) ||
			(props.maxDate && normalizedDate > props.maxDate)
		);
	};

	return (
		<FormItemBase {...props}>
			<div ref={containerWrapper}>
				<DatePickerInput
					refreshSignal={refreshSignal}
					onChange={onInputChange}
					toggleCalendar={shouldOpen => setOpen(shouldOpen ?? !open)}
					inputWrapperRef={inputWrapper}
					value={inputValue}
					format={props.format}
					testId={props.testId}
					error={props.error}
					placeholder={props.placeholder}
					disabled={props.disabled}
					preserveInvalidValue={props.preserveInvalidValue}
				/>
				<div ref={dropdownWrapper} className={classes.calendarContainer} style={{ display: 'none' }}>
					<Datetime
						className={classes.root}
						value={value}
						timeFormat={false}
						dateFormat={props.calendarDateFormat}
						locale={props.localeName}
						onChange={onCalendarChange}
						isValidDate={current => isDateValid(current.toDate())}
						input={false}
					/>
				</div>
			</div>
		</FormItemBase>
	);
};

export default DatePicker;
